import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import Loader from "../../../loader/Loader";
import { getQuickFileAlbum } from "../../../../redux/quickFile/quickFileSlice";
import { AppDispatch, StoreState } from "../../../../redux/store";
import { REQUEST_LOADING_STATES } from "../../../../helpers/constant";
import QuickFileList from "../../../../pages/CreatorPages/CreatorQuickFileComponent/QuickFileList";
import ModalQuickFileTab, { ModalQuickFileTabValue } from "./ModalQuickFileTab";
import "./SelectQuickFileAlbumModal.less";

export type SelectQuickFileAlbumModalProps = {
  onClose: () => void;
  onAlbumSelect: (albumId: string, type: "media" | "audio") => void;
  hideAudioTab?: boolean;
  hideMediaTab?: boolean;
};

const SelectQuickFileAlbumModal: React.FC<SelectQuickFileAlbumModalProps> = ({
  onClose,
  onAlbumSelect,
  hideAudioTab = false,
  hideMediaTab = false,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const { list: quickFileAlbumList, loading } = useSelector((state: StoreState) => state.quickFile);
  const shouldFetch = useRef(true);
  const [activeTab, setActiveTab] = useState<ModalQuickFileTabValue>(hideMediaTab ? "audio" : "media");

  useEffect(() => {
    if (shouldFetch.current) {
      shouldFetch.current = false;
      dispatch(getQuickFileAlbum());
    }
  }, [dispatch]);

  const handleOverlayClick = () => onClose?.();
  const handleModalClick = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => e.stopPropagation();

  // Prevent background scroll when modal is open
  useEffect(() => {
    const originalStyle = window.getComputedStyle(document.body).overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = originalStyle;
    };
  }, []);

  const modalContent = (
    <div className="select-quick-file-album-overlay" onClick={handleOverlayClick}>
      <div className="select-quick-file-album-content" onClick={handleModalClick}>
        <div className="modal-header-line-wrapper">
          <div className="modal-header-line"></div>
        </div>
        <div className="select-quick-file-album-container">
          <div className="select-quick-file-album-title">{t("My-albums")}</div>
          {!hideAudioTab && !hideMediaTab && <ModalQuickFileTab onChange={setActiveTab} initialValue={hideMediaTab ? "audio" : "media"} />}
          <div className="select-quick-file-album-body">
            {loading ? (
              <div className="select-quick-file-album-loader">
                <Loader />
              </div>
            ) : (
              <div className="modal-quick-file-wrapper">
                <QuickFileList
                  quickFiles={quickFileAlbumList}
                  loading={loading as unknown as REQUEST_LOADING_STATES}
                  queryParams={{ id: "", conversationId: "", messageId: "", receiverId: "", collectionTitle: "", block: "" }}
                  filterType={hideAudioTab ? "photos-videos" : hideMediaTab ? "audio" : activeTab === "media" ? "photos-videos" : "audio"}
                  onAlbumSelect={(id) => {
                    onAlbumSelect(id, hideAudioTab ? "media" : hideMediaTab ? "audio" : activeTab);
                  }}
                />
              </div>
            )}
          </div>
          <button type="button" className="select-quick-file-album-cancel-button" onClick={onClose}>
            {t("cancel")}
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default SelectQuickFileAlbumModal;
